// Push events from the server via a per-device Azure Storage Queue (read with a SAS URL; no storage keys on the client).

export type MailEventType =
  | 'mail.received' | 'mail.updated' | 'mail.sent' | 'draft.saved' | 'folders.changed' | 'contacts.changed'
  | 'prefs.changed' | 'template.changed' | 'lists.changed' | 'test' | string;

export interface MailEvent {
  type: MailEventType;
  at: string;
  data?: any;
}

export interface EventSubscription {
  deviceId: string;
  queue: string;
  /** Queue URL including a read+process SAS. */
  queueUrl: string;
  expiresUtc: string;
}

interface QueueMessage {
  id: string;
  popReceipt: string;
  text: string;
}

function decodeXml(s: string): string {
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
}

function fromBase64(b64: string): string {
  const B = (globalThis as any).Buffer;
  if (B) return B.from(b64, 'base64').toString('utf8');
  const bin = atob(b64);
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

export function parseQueueMessages(xml: string): QueueMessage[] {
  const out: QueueMessage[] = [];
  for (const m of xml.matchAll(/<QueueMessage>([\s\S]*?)<\/QueueMessage>/g)) {
    const tag = (n: string) => m[1].match(new RegExp(`<${n}>([\\s\\S]*?)</${n}>`))?.[1] ?? '';
    out.push({ id: tag('MessageId'), popReceipt: decodeXml(tag('PopReceipt')), text: decodeXml(tag('MessageText')) });
  }
  return out;
}

function withPath(queueUrl: string, path: string, query: string): string {
  const u = new URL(queueUrl);
  u.pathname = `${u.pathname.replace(/\/$/, '')}${path}`;
  return `${u.toString()}${u.search ? '&' : '?'}${query}`;
}

export interface EventPollerOptions {
  subscribe(): Promise<EventSubscription>;
  onEvent(e: MailEvent): void;
  onStatus?(status: 'connected' | 'error', detail?: string): void;
  fetch?: typeof fetch;
  /** Fastest poll interval while events are flowing (ms). */
  minDelay?: number;
  /** Slowest poll interval when idle (ms). */
  maxDelay?: number;
}

/**
 * Polls the device's queue: fast right after activity, backing off to maxDelay when idle. Each message is deleted after
 * it is handed to onEvent. Re-subscribes automatically before the SAS expires or when the queue is rejected.
 */
export class EventPoller {
  private sub: EventSubscription | null = null;
  private stopped = true;
  private delay: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly opts: EventPollerOptions;
  private readonly f: typeof fetch;

  constructor(opts: EventPollerOptions) {
    this.opts = opts;
    this.f = opts.fetch ?? fetch.bind(globalThis);
    this.delay = opts.minDelay ?? 1500;
  }

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    this.schedule(0);
  }

  stop() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Poll right away (e.g. when the window gains focus). */
  nudge() {
    this.delay = this.opts.minDelay ?? 1500;
    if (!this.stopped) this.schedule(0);
  }

  private schedule(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.tick(), ms);
  }

  private async ensure(): Promise<EventSubscription> {
    if (!this.sub || Date.parse(this.sub.expiresUtc) - Date.now() < 60 * 60_000) this.sub = await this.opts.subscribe();
    return this.sub;
  }

  private async tick() {
    if (this.stopped) return;
    const min = this.opts.minDelay ?? 1500;
    const max = this.opts.maxDelay ?? 15_000;
    try {
      const sub = await this.ensure();
      const res = await this.f(withPath(sub.queueUrl, '/messages', 'numofmessages=32&visibilitytimeout=30'));
      if (res.status === 403 || res.status === 404) {
        this.sub = null; // SAS expired or queue gone: subscribe again next tick
        throw new Error(`queue ${res.status}`);
      }
      if (!res.ok) throw new Error(`queue ${res.status}`);
      const messages = parseQueueMessages(await res.text());
      this.opts.onStatus?.('connected');
      for (const m of messages) {
        try {
          this.opts.onEvent(JSON.parse(fromBase64(m.text)) as MailEvent);
        } catch {
          /* malformed message: drop it */
        }
        await this.f(withPath(sub.queueUrl, `/messages/${m.id}`, `popreceipt=${encodeURIComponent(m.popReceipt)}`), { method: 'DELETE' }).catch(() => undefined);
      }
      this.delay = messages.length ? min : Math.min(max, Math.round(this.delay * 1.5));
    } catch (e) {
      this.opts.onStatus?.('error', e instanceof Error ? e.message : String(e));
      this.delay = Math.min(60_000, Math.max(this.delay * 2, 5000));
    }
    if (!this.stopped) this.schedule(this.delay);
  }
}
